import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  linkedSignal,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { getStitchDateZone, isStitchBoundaryFloating } from '@modules/tasks/utils/stitch-time.util';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { fileVersionError } from '@modules/files/version-history/file-versions.client';
import { type Subscription } from 'rxjs';
import { type Project } from '../types/project';
import { type SavedProjectVersion, type ProjectVersion, ProjectVersionsClient } from './project-versions.client';

const LOAD_FAILED = 'Version history could not load. Try again.';

type ProjectField =
  | 'title'
  | 'description'
  | 'color'
  | 'priority'
  | 'startDateTime'
  | 'endDateTime'
  | 'hasStartTime'
  | 'hasEndTime';

/** The fields a project version carries, by the names a person reads (#2079's `fields`). */
const FIELDS: ReadonlyArray<readonly [ProjectField, string]> = [
  ['title', 'Title'],
  ['description', 'Description'],
  ['color', 'Color'],
  ['priority', 'Priority'],
  ['startDateTime', 'Start'],
  ['endDateTime', 'End'],
  ['hasStartTime', 'Has start time'],
  ['hasEndTime', 'Has end time'],
];
const FIELD_NAMES = new Map<string, string>(FIELDS);
const PRIORITY_NAMES: Record<string, string> = { low: 'Low', normal: 'Normal', high: 'High' };

/** One field of a compared version: its saved value beside the project's current one. */
export interface ProjectCompareRow {
  readonly label: string;
  readonly kind: 'text' | 'date';
  readonly saved: string | Date | null;
  readonly current: string | Date | null;
  readonly savedFormat?: 'date' | 'dateTime';
  readonly currentFormat?: 'date' | 'dateTime';
  readonly savedTimeZone?: string;
  readonly currentTimeZone?: string;
}
type Comparison =
  | { readonly state: 'loading' }
  | { readonly state: 'failed'; readonly message: string }
  | {
      readonly state: 'ready';
      readonly rows: readonly ProjectCompareRow[];
    };

/** A team project's saved versions: compare each with the current project, and Restore content for an editor. */
@Component({
  selector: 'app-project-version-history',
  imports: [DateFormatPipe],
  // Through this subtree's space-context client, which stamps the project's own workspace (#2074).
  providers: [ProjectVersionsClient],
  templateUrl: './project-version-history.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectVersionHistoryComponent {
  public readonly project = input.required<Project>();
  public readonly canEdit = input(false);
  /** The form is saving or holds unsaved changes: a restore would be overwritten or lost (R4). */
  public readonly held = input(false);
  /** The form takes the restored project as its current one. */
  public readonly restored = input<(project: Partial<Project>) => void>(() => undefined);
  private readonly client = inject(ProjectVersionsClient);
  private readonly injector = inject(Injector);
  private readonly space = inject(SpaceContextStore);
  private readonly displayTimeZone = inject(DisplayTimeZoneService);
  private readonly cancelButton = viewChild<ElementRef<HTMLButtonElement>>('restoreCancel');
  private readonly focusCancel = effect(() => {
    const button = this.cancelButton();
    if (!button) return;
    afterNextRender(
      () => {
        if (this.cancelButton() === button) button.nativeElement.focus();
      },
      { injector: this.injector },
    );
  });
  protected readonly available = linkedSignal(() => {
    const project = this.project();
    return !!project.id && this.space.insideTeam() && project.accountId === this.space.contextId();
  });
  protected readonly page = linkedSignal(() => {
    this.project();
    this.space.contextId();
    return 0;
  });
  protected readonly rows = signal<readonly ProjectVersion[]>([]);
  protected readonly current = signal<number | null>(null);
  protected readonly total = signal(0);
  protected readonly loading = signal(false);
  protected readonly failure = signal<string | null>(null);
  protected readonly confirmation = signal<number | null>(null);
  protected readonly busy = signal(false);
  protected readonly comparisons = signal<ReadonlyMap<number, Comparison>>(new Map());
  private readonly reread = signal(0);
  private action?: Subscription;
  private generation = 0;

  private readonly identity = effect(() => {
    this.project();
    this.available();
    this.space.contextId();
    untracked(() => {
      this.generation++;
      this.action?.unsubscribe();
      this.busy.set(false);
      this.confirmation.set(null);
      this.comparisons.set(new Map());
    });
  });
  private readonly load = effect((onCleanup) => {
    const project = this.project();
    const available = this.available();
    const offset = this.page() * 25;
    this.reread();
    untracked(() => {
      this.rows.set([]);
      this.total.set(0);
      this.current.set(null);
      this.failure.set(null);
      this.loading.set(available);
    });
    if (!available) return;
    const subscription = this.client.list(project, offset).subscribe({
      next: (answer) => {
        this.rows.set(answer.items);
        this.current.set(answer.current);
        this.total.set(answer.total);
        this.loading.set(false);
      },
      error: (error: unknown) => {
        this.failure.set(fileVersionError(error, LOAD_FAILED));
        this.loading.set(false);
      },
    });
    onCleanup(() => subscription.unsubscribe());
  });

  public constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.generation++;
      this.action?.unsubscribe();
    });
  }
  protected changed(row: ProjectVersion): string {
    return row.fields.map((field) => FIELD_NAMES.get(field) ?? field).join(', ');
  }
  /** A version's content is read once, the first time its comparison opens. */
  protected compare(version: number, event: Event): void {
    if (
      !(event.target instanceof HTMLDetailsElement) ||
      !event.target.open ||
      this.comparisons().has(version) ||
      !this.available()
    )
      return;
    const project = this.project();
    const generation = this.generation;
    this.setComparison(version, { state: 'loading' });
    this.client.read(project, version).subscribe({
      next: (saved) => {
        if (this.generation === generation)
          this.setComparison(version, { state: 'ready', rows: this.compareRows(saved) });
      },
      error: (error: unknown) => {
        if (this.generation !== generation) return;
        this.setComparison(version, {
          state: 'failed',
          message: fileVersionError(error, 'This version could not load. Try again.'),
        });
      },
    });
  }
  protected comparisonState(version: number): Comparison['state'] | undefined {
    return this.comparisons().get(version)?.state;
  }
  protected comparisonRows(version: number): readonly ProjectCompareRow[] {
    const comparison = this.comparisons().get(version);
    return comparison?.state === 'ready' ? comparison.rows : [];
  }
  protected comparisonFailure(version: number): string | null {
    const comparison = this.comparisons().get(version);
    return comparison?.state === 'failed' ? comparison.message : null;
  }
  protected askRestore(version: number): void {
    if (!this.canEdit() || this.busy() || version === this.current()) return;
    this.confirmation.set(version);
    this.failure.set(null);
  }
  protected cancel(): void {
    if (this.busy()) return;
    this.confirmation.set(null);
    this.failure.set(null);
  }
  protected confirmRestore(): void {
    const version = this.confirmation();
    if (version === null || version === this.current() || !this.available() || !this.canEdit()) return;
    if (this.busy() || this.held()) return;
    const project = this.project();
    const generation = this.generation;
    this.busy.set(true);
    this.failure.set(null);
    this.action = this.client.restore(project, version).subscribe({
      next: (answer) => {
        if (this.generation !== generation || this.project() !== project) return;
        this.confirmation.set(null);
        this.busy.set(false);
        if (answer.restored !== false) this.restored()(answer.project);
        this.comparisons.set(new Map());
        this.page.set(0);
        this.reread.update((value) => value + 1);
      },
      error: (error: unknown) => {
        if (this.generation !== generation) return;
        this.failure.set(fileVersionError(error, 'The version could not be restored. Try again.'));
        this.busy.set(false);
      },
    });
  }
  protected retry(): void {
    if (!this.busy()) this.reread.update((value) => value + 1);
  }
  protected previous(): void {
    if (this.busy()) return;
    this.confirmation.set(null);
    this.page.update((page) => Math.max(0, page - 1));
  }
  protected next(): void {
    if (this.busy() || (this.page() + 1) * 25 >= this.total()) return;
    this.confirmation.set(null);
    this.page.update((page) => page + 1);
  }
  private setComparison(version: number, comparison: Comparison): void {
    this.comparisons.update((all) => new Map(all).set(version, comparison));
  }
  private compareRows(saved: SavedProjectVersion): ProjectCompareRow[] {
    const project = this.project();
    return FIELDS.map(([field, label]) => {
      const date = field === 'startDateTime' || field === 'endDateTime';
      const position = field === 'startDateTime' ? 'start' : 'end';
      const savedFlags = { hasStartTime: saved.hasStartTime === true, hasEndTime: saved.hasEndTime === true };
      const savedFloating = isStitchBoundaryFloating(savedFlags, position);
      const currentFloating = isStitchBoundaryFloating(project, position);
      const accountZone = this.displayTimeZone.timeZone();
      return {
        label,
        kind: date ? 'date' : 'text',
        savedFormat: savedFloating ? 'date' : 'dateTime',
        currentFormat: currentFloating ? 'date' : 'dateTime',
        savedTimeZone: getStitchDateZone(savedFlags, position, accountZone),
        currentTimeZone: getStitchDateZone(project, position, accountZone),
        saved: date ? (saved[field] ?? null) : this.text(field, saved[field]),
        current: date ? (project[field] ?? null) : this.text(field, project[field]),
      };
    });
  }
  private text(field: ProjectField, value: unknown): string | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (field === 'priority') return PRIORITY_NAMES[String(value)] ?? String(value);
    return String(value);
  }
}
